import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import HttpService from "../../../services/HttpService";
import SearchableCompanySelect from "./SearchableCompanySelect";

const AccountActions = ({ account, onChanged, onDeleted }) => {
	const { t } = useTranslation();
	const [roles, setRoles] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [selectedDebtors, setSelectedDebtors] = useState([]);
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		let mounted = true;
		const http = HttpService.getAxiosClient();
		HttpService.configure();
		const currentDebtors = (account.attributes?.debinr || []).map(String);
		const currentRoles = (account.roles || []).map(String);
		setSelectedDebtors(currentDebtors);
		setSelectedRoles(currentRoles);

		Promise.all([
			http.get("/keycloak/roles"),
			http.get("/Klanten/GetDebis"),
		]).then(([rolesResponse, debtorsResponse]) => {
			if (!mounted) return;
			const assignedDebtors = Array.isArray(debtorsResponse.data) ? debtorsResponse.data.map(String) : [];
			setRoles(rolesResponse.data || []);
			return assignedDebtors.length > 0
				? http.get(`/Klanten?limited=true&${assignedDebtors.map((debiNr) => `debiNr=${encodeURIComponent(debiNr)}`).join("&")}`).then((companiesResponse) => ({ assignedDebtors, companiesResponse }))
				: { assignedDebtors, companiesResponse: { data: [] } };
		}).then((result) => {
			if (!mounted || !result) return;
			setCompanies(Object.entries(result.companiesResponse.data || {}).map(([debiNr, name]) => ({
				name,
				debiNr: String(debiNr),
			})));
		}).catch(() => {
			if (mounted) setError(t("accounts.actionsLoadError"));
		}).finally(() => {
			if (mounted) setLoading(false);
		});

		return () => {
			mounted = false;
		};
	}, [account, t]);

	const toggleValue = (value, setter) => {
		setter((current) => current.includes(value)
			? current.filter((item) => item !== value)
			: [...current, value]);
	};

	const runAction = async (action, body, successCallback = onChanged) => {
		setBusy(true);
		setError("");
		try {
			await action(body);
			successCallback();
		} catch (err) {
			setError(t("accounts.actionError"));
		} finally {
			setBusy(false);
		}
	};

	const resetPassword = () => {
		if (window.confirm(t("accounts.confirmResetPassword"))) {
			runAction(() => HttpService.getAxiosClient().put(`/Accounts/ResetPassword/${account.keycloakId}`));
		}
	};

	const resetOtp = () => {
		if (window.confirm(t("accounts.confirmResetOtp"))) {
			runAction(() => HttpService.getAxiosClient().put(`/Accounts/ResetOTP/${account.keycloakId}`));
		}
	};

	const updateAssignments = async () => {
		const currentDebtors = new Set((account.attributes?.debinr || []).map(String));
		const currentRoles = new Set((account.roles || []).map(String));
		const debtorsToAdd = selectedDebtors.filter((debtor) => !currentDebtors.has(debtor));
		const debtorsToRemove = [...currentDebtors].filter((debtor) => !selectedDebtors.includes(debtor));
		const rolesToAdd = selectedRoles.filter((role) => !currentRoles.has(role));
		const rolesToRemove = [...currentRoles].filter((role) => !selectedRoles.includes(role));

		if (debtorsToAdd.length === 0 && debtorsToRemove.length === 0 && rolesToAdd.length === 0 && rolesToRemove.length === 0) return;

		setBusy(true);
		setError("");
		try {
			const http = HttpService.getAxiosClient();
			if (debtorsToAdd.length > 0) await http.put(`/Accounts/AddDebinrs/${account.keycloakId}`, debtorsToAdd);
			if (debtorsToRemove.length > 0) await http.put(`/Accounts/RemoveDebinrs/${account.keycloakId}`, debtorsToRemove);

			if (rolesToRemove.length > 0) await http.put(`/Accounts/RemoveRoles/${account.keycloakId}`, rolesToRemove);
			if (rolesToAdd.length > 0) await http.put(`/Accounts/AddRole/${account.keycloakId}`, rolesToAdd, { headers: { "Content-Type": "application/json" } });
			onChanged();
		} catch (err) {
			setError(t("accounts.actionError"));
		} finally {
			setBusy(false);
		}
	};

	const removeAccount = () => {
		if (window.confirm(t("accounts.confirmDelete"))) {
			runAction(() => HttpService.getAxiosClient().delete(`/Accounts/${account.keycloakId}`), undefined, onDeleted);
		}
	};

	return (
		<div className="account-actions-panel">
			<button className="btn btn-secondary" type="button" disabled={busy} onClick={resetPassword}>{t("accounts.resetPassword")}</button>
			<button className="btn btn-secondary" type="button" disabled={busy} onClick={resetOtp}>{t("accounts.resetOtp")}</button>

			{loading ? <span>{t("accounts.actionsLoading")}</span> : (
				<>
					<div className="account-action-group">
						<span className="filter-label">{t("accounts.companies")}</span>
						{companies.length > 0 ? (
							<SearchableCompanySelect
								companies={companies}
								selectedDebtors={selectedDebtors}
								onChange={setSelectedDebtors}
								inputId={`account-action-companies-${account.keycloakId}`}
								placeholder={t("accounts.selectCompanies")}
								noOptionsMessage={t("accounts.noCompanies")}
							/>
						) : <span>{t("accounts.noCompanies")}</span>}
					</div>

					<div className="account-action-group">
						<span className="filter-label">{t("accounts.roles")}</span>
						{roles.length > 0 ? roles.map((role) => (
							<label className="account-selection-option" key={role.name} title={role.description}>
								<input type="checkbox" checked={selectedRoles.includes(role.name)} onChange={() => toggleValue(role.name, setSelectedRoles)} />
								<span><strong>{role.name}</strong><small>{role.description}</small></span>
							</label>
						)) : <span>{t("accounts.noAdditionalRoles")}</span>}
					</div>
					<button className="btn btn-secondary" type="button" disabled={busy} onClick={updateAssignments}>{t("accounts.updateAssignments")}</button>
				</>
			)}

			{error && <p className="account-action-error">{error}</p>}
			<button className="btn btn-error" type="button" disabled={busy} onClick={removeAccount}>{t("accounts.removeAccount")}</button>
		</div>
	);
};

export default AccountActions;
